/**
 * Copia del mapa de plantillas del Cloudflare Worker.
 *
 * Fuente de verdad: `cloudflare-worker-delfuocampo/worker.js` → `COUNTRY_TEMPLATE_MAP` + `DEFAULT_CONFIG`.
 * Tras cambios en el Worker, actualizar este archivo y desplegar el front.
 * Paridad: `npm run verify:dual-cta-geo`.
 *
 * Política actual:
 *  - AR → BaseTemplate (provincia controla CTA: community / birthday / wishlist / none)
 *  - Resto del mundo (y DEFAULT) → DualCTAVideoLanding (video Mux fullscreen + 2 CTAs)
 *
 * dualCta (mux/labels/stripe/free CTA): NO vive en este mapa. El Worker inyecta
 * `window.__TEMPLATE_CONFIG__.dualCta` desde `country_template_config.dual_cta_config`
 * (KV): fila país mergeada sobre DEFAULT (Worker ≥ 2.9.13). Override SPA
 * `?country=XX` sin debug del Worker solo cambia template/language — para dualCta
 * fiel usar `?debug=$WORKER_DEBUG_SECRET&country=XX` o `?checkout_country=XX`.
 *
 * El reparto real por país se administra en admin/country-template (DB) y lo sirve el Worker;
 * este mapa es la copia para overrides `?country=XX` (debug) y tests, y popula la tabla
 * del admin (`/admin/country-template`). Cualquier país no listado acá no aparece en el
 * admin (aunque el Worker igual lo sirve con `DEFAULT_CONFIG`).
 * Admin puede forzar BaseTemplate en un país intl (p. ej. ES) → dualCta no se inyecta.
 */

import type { ServerTemplateConfig } from "@/lib/serverTemplateDetection";

export type WorkerTemplateRow = {
  template: string;
  bioLayout: null;
  language: string;
};

/** Igual que `DEFAULT_CONFIG` en worker.js */
export const WORKER_DEFAULT_TEMPLATE: WorkerTemplateRow = {
  template: "DualCTAVideoLanding",
  bioLayout: null,
  language: "en-US",
};

const DUAL = "DualCTAVideoLanding";

/**
 * Igual que `COUNTRY_TEMPLATE_MAP` + `getARConfig` / `getUSConfig` en worker.js
 * (sin rutas especiales como /sorprendeme).
 */
export const WORKER_COUNTRY_TEMPLATE_MAP: Record<string, WorkerTemplateRow> = {
  // ── ARGENTINA (única excepción: BaseTemplate + lock provincial) ──
  AR: { template: "BaseTemplate", bioLayout: null, language: "es-AR" },

  // ── LATAM hispanohablante ──
  EC: { template: DUAL, bioLayout: null, language: "es-EC" },
  MX: { template: DUAL, bioLayout: null, language: "es-MX" },
  CL: { template: DUAL, bioLayout: null, language: "es-CL" },
  CO: { template: DUAL, bioLayout: null, language: "es-CO" }, // root/home uses DualCTAVideoLanding + Stripe; dedicated /colombia /bogota /medellin /cali /cartagena (+ /en/*) use BaseTemplate explicitly for rich SEO positioning + same admin control surface (heroCta, site-settings, country-template CO) with Stripe checkout.
  PE: { template: DUAL, bioLayout: null, language: "es-PE" },
  UY: { template: DUAL, bioLayout: null, language: "es-UY" },
  VE: { template: DUAL, bioLayout: null, language: "es-VE" },
  BO: { template: DUAL, bioLayout: null, language: "es-BO" },
  CR: { template: DUAL, bioLayout: null, language: "es-CR" },
  PA: { template: DUAL, bioLayout: null, language: "es-PA" },
  DO: { template: DUAL, bioLayout: null, language: "es-DO" },
  GT: { template: DUAL, bioLayout: null, language: "es-GT" },
  HN: { template: DUAL, bioLayout: null, language: "es-HN" },
  NI: { template: DUAL, bioLayout: null, language: "es-NI" },
  SV: { template: DUAL, bioLayout: null, language: "es-SV" },
  PY: { template: DUAL, bioLayout: null, language: "es-PY" },
  CU: { template: DUAL, bioLayout: null, language: "es-CU" },
  PR: { template: DUAL, bioLayout: null, language: "es-PR" },

  // ── BRASIL ──
  BR: { template: DUAL, bioLayout: null, language: "pt-BR" },

  // ── ESPAÑA ──
  ES: { template: DUAL, bioLayout: null, language: "es-ES" },

  // ── NORTEAMÉRICA & ANGLOSAJÓN ──
  US: { template: DUAL, bioLayout: null, language: "en-US" },
  CA: { template: DUAL, bioLayout: null, language: "en-CA" },
  GB: { template: DUAL, bioLayout: null, language: "en-GB" },
  IE: { template: DUAL, bioLayout: null, language: "en-IE" },
  AU: { template: DUAL, bioLayout: null, language: "en-AU" },
  NZ: { template: DUAL, bioLayout: null, language: "en-NZ" },

  // ── CARIBE ANGLO ──
  JM: { template: DUAL, bioLayout: null, language: "en-JM" },
  TT: { template: DUAL, bioLayout: null, language: "en-TT" },

  // ── EUROPA OCCIDENTAL & CENTRAL ──
  DE: { template: DUAL, bioLayout: null, language: "de-DE" },
  FR: { template: DUAL, bioLayout: null, language: "fr-FR" },
  IT: { template: DUAL, bioLayout: null, language: "it-IT" },
  PT: { template: DUAL, bioLayout: null, language: "pt-PT" },
  NL: { template: DUAL, bioLayout: null, language: "nl-NL" },
  BE: { template: DUAL, bioLayout: null, language: "fr-BE" },
  CH: { template: DUAL, bioLayout: null, language: "de-CH" },
  AT: { template: DUAL, bioLayout: null, language: "de-AT" },
  GR: { template: DUAL, bioLayout: null, language: "el-GR" },
  DK: { template: DUAL, bioLayout: null, language: "da-DK" },
  SE: { template: DUAL, bioLayout: null, language: "sv-SE" },
  NO: { template: DUAL, bioLayout: null, language: "nb-NO" },
  FI: { template: DUAL, bioLayout: null, language: "fi-FI" },
  PL: { template: DUAL, bioLayout: null, language: "pl-PL" },
  CZ: { template: DUAL, bioLayout: null, language: "cs-CZ" },
  HU: { template: DUAL, bioLayout: null, language: "hu-HU" },
  RO: { template: DUAL, bioLayout: null, language: "ro-RO" },
  BG: { template: DUAL, bioLayout: null, language: "bg-BG" },
  HR: { template: DUAL, bioLayout: null, language: "hr-HR" },
  SI: { template: DUAL, bioLayout: null, language: "sl-SI" },
  SK: { template: DUAL, bioLayout: null, language: "sk-SK" },
  LT: { template: DUAL, bioLayout: null, language: "lt-LT" },
  LV: { template: DUAL, bioLayout: null, language: "lv-LV" },
  EE: { template: DUAL, bioLayout: null, language: "et-EE" },
  LU: { template: DUAL, bioLayout: null, language: "fr-LU" },
  MT: { template: DUAL, bioLayout: null, language: "mt-MT" },
  CY: { template: DUAL, bioLayout: null, language: "el-CY" },
  IS: { template: DUAL, bioLayout: null, language: "is-IS" },

  // ── EUROPA ORIENTAL / BALCANES / CIS ──
  UA: { template: DUAL, bioLayout: null, language: "uk-UA" },
  RU: { template: DUAL, bioLayout: null, language: "ru-RU" },
  RS: { template: DUAL, bioLayout: null, language: "sr-RS" },
  BA: { template: DUAL, bioLayout: null, language: "bs-BA" },
  AL: { template: DUAL, bioLayout: null, language: "sq-AL" },
  ME: { template: DUAL, bioLayout: null, language: "sr-ME" },
  MK: { template: DUAL, bioLayout: null, language: "mk-MK" },
  MD: { template: DUAL, bioLayout: null, language: "ro-MD" },
  GE: { template: DUAL, bioLayout: null, language: "ka-GE" },
  AM: { template: DUAL, bioLayout: null, language: "hy-AM" },
  AZ: { template: DUAL, bioLayout: null, language: "az-AZ" },
  KZ: { template: DUAL, bioLayout: null, language: "ru-KZ" },

  // ── MEDIO ORIENTE ──
  AE: { template: DUAL, bioLayout: null, language: "ar-AE" },
  SA: { template: DUAL, bioLayout: null, language: "ar-SA" },
  EG: { template: DUAL, bioLayout: null, language: "ar-EG" },
  QA: { template: DUAL, bioLayout: null, language: "ar-QA" },
  KW: { template: DUAL, bioLayout: null, language: "ar-KW" },
  OM: { template: DUAL, bioLayout: null, language: "ar-OM" },
  IL: { template: DUAL, bioLayout: null, language: "he-IL" },
  JO: { template: DUAL, bioLayout: null, language: "ar-JO" },
  IQ: { template: DUAL, bioLayout: null, language: "ar-IQ" },
  LB: { template: DUAL, bioLayout: null, language: "ar-LB" },
  TR: { template: DUAL, bioLayout: null, language: "tr-TR" },
  MA: { template: DUAL, bioLayout: null, language: "ar-MA" },
  TN: { template: DUAL, bioLayout: null, language: "ar-TN" },
  DZ: { template: DUAL, bioLayout: null, language: "ar-DZ" },

  // ── ASIA ──
  ID: { template: DUAL, bioLayout: null, language: "id-ID" },
  JP: { template: DUAL, bioLayout: null, language: "ja-JP" },
  KR: { template: DUAL, bioLayout: null, language: "ko-KR" },
  SG: { template: DUAL, bioLayout: null, language: "en-SG" },
  MY: { template: DUAL, bioLayout: null, language: "ms-MY" },
  TH: { template: DUAL, bioLayout: null, language: "th-TH" },
  PH: { template: DUAL, bioLayout: null, language: "en-PH" },
  VN: { template: DUAL, bioLayout: null, language: "vi-VN" },
  IN: { template: DUAL, bioLayout: null, language: "hi-IN" },
  PK: { template: DUAL, bioLayout: null, language: "ur-PK" },
  BD: { template: DUAL, bioLayout: null, language: "bn-BD" },
  TW: { template: DUAL, bioLayout: null, language: "zh-TW" },
  HK: { template: DUAL, bioLayout: null, language: "zh-HK" },
  CN: { template: DUAL, bioLayout: null, language: "zh-CN" },

  // ── ÁFRICA ──
  ZA: { template: DUAL, bioLayout: null, language: "en-ZA" },
  NG: { template: DUAL, bioLayout: null, language: "en-NG" },
  KE: { template: DUAL, bioLayout: null, language: "en-KE" },
  GH: { template: DUAL, bioLayout: null, language: "en-GH" },
};

/**
 * Configuración alineada con el Worker para overrides `?country=XX` (debug) y tests.
 */
export function getWorkerTemplateConfigForCountry(country: string): ServerTemplateConfig {
  const code = country.toUpperCase().trim().slice(0, 2);
  const row = WORKER_COUNTRY_TEMPLATE_MAP[code] ?? WORKER_DEFAULT_TEMPLATE;
  return {
    country: code,
    templateName: row.template,
    bioLayout: row.bioLayout,
    language: row.language,
    province: null,
    cfCountry: code,
    timezone: "",
    city: "",
    detectionMethod: "manual_override_worker_map",
  };
}
